/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/**
 * @author       esraa.afifi@zumper.com (Esraa Afifi)
 * @copyright    Copyright (c) 2022, Zumper
 */

import { useTheme } from '@chakra-ui/react'
import { ALPHA_70 } from '@zcode-ui/themes'
import React, { memo, useMemo } from 'react'
import { CarouselInternalState } from 'react-multi-carousel'

import * as styles from './styles'

export interface ProgressDotsProps extends Partial<CarouselInternalState> {
  disabled?: (index: number) => boolean
  isMobile?: boolean
  goToSlide: ((index: number) => void) | undefined
  totalItems: number
  currentSlide: number
  maxDots?: number
}

export const ProgressDots = memo(
  ({ disabled, isMobile, goToSlide, totalItems, maxDots, currentSlide }: ProgressDotsProps) => {
    const { colors } = useTheme()
    const indexOffset = useMemo(
      () => (maxDots && totalItems > maxDots ? Math.floor(currentSlide / maxDots) * maxDots : 0),
      [maxDots, totalItems, currentSlide]
    )

    const offsetExists = indexOffset !== undefined
    const maxIndex = maxDots ?? totalItems

    const indexArray = useMemo(() => Array.from(Array(maxIndex).keys()), [maxIndex])

    return (
      <ul css={[styles.dots, offsetExists && styles.maxDots]}>
        {indexArray.map((index) => {
          const activeDot = offsetExists
            ? indexOffset + index === currentSlide
            : currentSlide === index

          const calcIndex = indexOffset && currentSlide >= maxIndex ? index + indexOffset : index

          if (calcIndex >= totalItems) {
            return null
          }

          return (
            <li key={`dot-${calcIndex}`}>
              <button
                aria-label={`slide ${String(calcIndex + 1)} of ${String(totalItems)}`}
                css={styles.dot}
                disabled={isMobile || disabled?.(calcIndex) || !goToSlide}
                onClick={(e) => {
                  e.stopPropagation()
                  goToSlide?.(calcIndex)
                }}
                style={{
                  backgroundColor: activeDot
                    ? colors.backgroundLightest
                    : `${colors.backgroundLightest as string}${ALPHA_70}`,
                }}
                type="button"
              >
                {activeDot && <span css={styles.visuallyHidden}>Current Item</span>}
                <span css={styles.visuallyHidden}>{calcIndex + 1}</span>
              </button>
            </li>
          )
        })}
      </ul>
    )
  }
)
